import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Customer } from '../types/partner';
import { useAuth } from './AuthContext';

interface CustomerSelectionContextType {
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
  availableCustomers: Customer[];
  isLoading: boolean;
}

const CustomerSelectionContext = createContext<CustomerSelectionContextType | null>(null);

interface CustomerSelectionProviderProps {
  children: ReactNode;
}

export function CustomerSelectionProvider({ children }: CustomerSelectionProviderProps) {
  const { managedCustomers, isLoading: authLoading } = useAuth();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Auto-select first customer when customers are loaded
  useEffect(() => {
    if (!authLoading && managedCustomers.length > 0 && !selectedCustomer) {
      setSelectedCustomer(managedCustomers[0]);
    }
    setIsLoading(authLoading);
  }, [managedCustomers, authLoading, selectedCustomer]);

  // Reset selection if customer is no longer in managed list
  useEffect(() => {
    if (selectedCustomer && managedCustomers.length > 0) {
      const stillExists = managedCustomers.find(c => c.customerId === selectedCustomer.customerId);
      if (!stillExists) {
        setSelectedCustomer(managedCustomers[0] || null);
      }
    }
  }, [selectedCustomer, managedCustomers]);

  const value: CustomerSelectionContextType = {
    selectedCustomer,
    setSelectedCustomer,
    availableCustomers: managedCustomers,
    isLoading
  };

  return (
    <CustomerSelectionContext.Provider value={value}>
      {children}
    </CustomerSelectionContext.Provider>
  );
}

export function useCustomerSelection(): CustomerSelectionContextType {
  const context = useContext(CustomerSelectionContext);
  if (!context) {
    throw new Error('useCustomerSelection must be used within a CustomerSelectionProvider');
  }
  return context;
} 